import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  PageHeader,
  Card,
  DataTable,
  Badge,
  Button,
  SearchInput,
  Toast,
  EmptyState,
} from '../ui';
import useDebouncedValue from '../../hooks/useDebouncedValue';
import {
  listAkeneoPushProducts,
  listFamilies,
  pushToAkeneo,
} from '../../services/pimApi';
import './pim.css';

const PAGE_SIZES = [25, 50, 100, 250];

// Mirrors MAX_PUSH_BATCH in waterco.akeneo_product_push; the API rejects more.
// The push is synchronous, so this is a real limit rather than a guardrail.
const MAX_BATCH = 20;

const PUSH_BADGES = {
  success: { variant: 'success', label: 'Pushed' },
  skipped: { variant: 'neutral', label: 'Skipped' },
  not_found: { variant: 'warning', label: 'Not in Akeneo' },
  error: { variant: 'danger', label: 'Error' },
};

const STATUS_FILTERS = [
  { key: 'never_pushed', label: 'Never pushed', count: 'never_pushed' },
  { key: 'stale', label: 'Changed since push', count: null },
  { key: 'pushed', label: 'Pushed', count: 'pushed' },
  { key: 'error', label: 'Errors', count: 'errored' },
];

const EMPTY_HINTS = {
  never_pushed: 'Every product has been pushed at least once.',
  stale: 'Nothing has changed in the PIM since it was last pushed.',
  pushed: 'No products have been pushed yet — select rows and click Push.',
  error: 'No pushes have failed.',
};

const IMAGE_NOTES = {
  uploaded: 'Image uploaded',
  skipped_existing: 'Image left alone (Akeneo already has one)',
  skipped_no_asset: 'No primary image in the PIM',
  skipped_no_attribute: 'Akeneo image attribute not found — check AKENEO_IMAGE_ATTRIBUTE',
  disabled: 'Image push turned off',
  dry_run: 'Image would be considered',
  error: 'Image upload failed',
};

function formatWhen(value) {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString();
}

function LastPushCell({ row }) {
  const last = row.last_push;
  if (!last) return <span className="pim-muted">Never</span>;
  const cfg = PUSH_BADGES[last.status] || PUSH_BADGES.skipped;
  return (
    <span style={{ display: 'inline-flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
      <span title={last.error_detail || undefined}>
        <Badge variant={cfg.variant}>{cfg.label}</Badge>
      </span>
      <span className="pim-muted" style={{ fontSize: 12 }}>
        {formatWhen(last.pushed_at)}
        {last.pushed_by ? ` · ${last.pushed_by}` : ''}
      </span>
    </span>
  );
}

function ResultsCard({ result, dryRun, onDismiss }) {
  const items = result.items || [];
  const s = result.summary || {};
  return (
    <Card
      title={dryRun ? 'Dry run results' : 'Push results'}
      desc={
        `${s.success || 0} ok · ${s.error || 0} failed · ${s.not_found || 0} not in Akeneo` +
        `${s.skipped ? ` · ${s.skipped} skipped` : ''}`
      }
      actions={<Button variant="ghost" size="sm" onClick={onDismiss}>Dismiss</Button>}
      style={{ marginBottom: 12 }}
    >
      <ul className="pim-push-results">
        {items.map((item) => {
          const cfg = PUSH_BADGES[item.status] || PUSH_BADGES.skipped;
          const imageNote = item.image_status ? IMAGE_NOTES[item.image_status] : null;
          return (
            <li key={item.sku}>
              <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                <Badge variant={cfg.variant}>{cfg.label}</Badge>
                <Link to={`/pim/products/${encodeURIComponent(item.sku)}`}>{item.sku}</Link>
                {imageNote && (
                  <span className="pim-muted" style={{ fontSize: 12 }}>{imageNote}</span>
                )}
              </div>
              {item.error_detail && (
                // The Akeneo response body: the only place that says which
                // attribute was rejected and why.
                <pre className="pim-push-detail">{item.error_detail}</pre>
              )}
              {(item.skipped_attributes || []).length > 0 && (
                <ul className="pim-push-skipped">
                  {item.skipped_attributes.map((s2, i) => (
                    <li key={`${item.sku}-${s2.attribute}-${i}`}>
                      <code>{s2.attribute}</code> not sent — {s2.reason}
                    </li>
                  ))}
                </ul>
              )}
            </li>
          );
        })}
      </ul>
    </Card>
  );
}

export default function AkeneoPush() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [summary, setSummary] = useState({});
  const [truncated, setTruncated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [qDraft, setQDraft] = useState('');
  const q = useDebouncedValue(qDraft);
  const [familyCode, setFamilyCode] = useState('');
  const [families, setFamilies] = useState([]);
  const [status, setStatus] = useState('');
  const [offset, setOffset] = useState(0);
  const [limit, setLimit] = useState(PAGE_SIZES[0]);
  const [selected, setSelected] = useState(() => new Set());
  const [includeImage, setIncludeImage] = useState(true);
  const [forceImage, setForceImage] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [resultWasDryRun, setResultWasDryRun] = useState(false);
  const [toast, setToast] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit, offset };
      if (q) params.q = q;
      if (familyCode) params.family_code = familyCode;
      if (status) params.status = status;
      const data = await listAkeneoPushProducts(params);
      setRows(data.products || []);
      setTotal(data.total || 0);
      setSummary(data.summary || {});
      setTruncated(Boolean(data.stale_scan_truncated));
      setSelected(new Set());
    } catch (e) {
      setToast({
        message: e?.response?.data?.detail || 'Failed to load the Akeneo push queue',
        error: true,
      });
    } finally {
      setLoading(false);
    }
  }, [q, familyCode, status, offset, limit]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    listFamilies().then(setFamilies).catch(() => {});
  }, []);

  const pageSkus = useMemo(() => rows.map((r) => r.identifier), [rows]);
  const allPageSelected = pageSkus.length > 0 && pageSkus.every((s) => selected.has(s));

  const toggleRow = (sku) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(sku)) next.delete(sku);
      else next.add(sku);
      return next;
    });
  };

  const toggleAllPage = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allPageSelected) pageSkus.forEach((s) => next.delete(s));
      // Selecting a whole page can exceed the batch cap, so stop at it rather
      // than letting the request fail after the operator has committed.
      else pageSkus.slice(0, MAX_BATCH).forEach((s) => next.add(s));
      return next;
    });
  };

  const runPush = async (dryRun) => {
    const skus = Array.from(selected);
    if (skus.length === 0) return;
    if (skus.length > MAX_BATCH) {
      setToast({ message: `Select at most ${MAX_BATCH} SKUs per push`, error: true });
      return;
    }
    if (
      !dryRun &&
      !window.confirm(
        `Push ${skus.length} product(s) to Akeneo? This overwrites their Akeneo` +
          ' values with the PIM data' +
          (includeImage ? ' and uploads the primary image' : '') +
          '. Akeneo is live and the push cannot be undone from here.'
      )
    ) {
      return;
    }

    setBusy(true);
    try {
      const data = await pushToAkeneo({
        skus,
        dry_run: dryRun,
        include_image: includeImage,
        force_image: forceImage,
      });
      setResult(data);
      setResultWasDryRun(dryRun);
      const s = data.summary || {};
      setToast({
        message:
          `${dryRun ? 'Dry run' : 'Push'} complete: ${s.success || 0} ok, ` +
          `${s.error || 0} failed, ${s.not_found || 0} not in Akeneo`,
        error: (s.error || 0) > 0,
      });
      if (!dryRun) await load();
    } catch (e) {
      setToast({
        message: e?.response?.data?.detail || 'Push failed',
        error: true,
      });
    } finally {
      setBusy(false);
    }
  };

  const columns = [
    {
      key: 'select',
      width: 36,
      header: (
        <input
          type="checkbox"
          checked={allPageSelected}
          onChange={toggleAllPage}
          aria-label="Select all on page"
        />
      ),
      render: (r) => (
        <input
          type="checkbox"
          checked={selected.has(r.identifier)}
          onChange={() => toggleRow(r.identifier)}
          aria-label={`Select ${r.identifier}`}
        />
      ),
    },
    {
      key: 'identifier',
      header: 'SKU',
      render: (r) => (
        <Link to={`/pim/products/${encodeURIComponent(r.identifier)}`}>{r.identifier}</Link>
      ),
    },
    {
      key: 'name',
      header: 'Name',
      render: (r) => (
        <span title={r.name || ''}>
          {r.product_name || r.name || <span className="pim-muted">—</span>}
        </span>
      ),
    },
    {
      key: 'family_code',
      header: 'Family',
      render: (r) => r.family_code || <span className="pim-muted">—</span>,
    },
    {
      key: 'has_image',
      header: 'Image',
      render: (r) =>
        r.has_image ? <Badge variant="info">Yes</Badge> : <span className="pim-muted">None</span>,
    },
    {
      key: 'last_push',
      header: 'Last push',
      render: (r) => <LastPushCell row={r} />,
    },
    {
      key: 'stale',
      header: 'Changed',
      render: (r) =>
        r.stale ? (
          <span title="The PIM data has changed since the last successful push">
            <Badge variant="warning">Changed</Badge>
          </span>
        ) : (
          <span className="pim-muted">—</span>
        ),
    },
  ];

  return (
    <>
      <PageHeader
        title="Push to Akeneo"
        sub="Interim one-way push: overwrite a product's Akeneo values with the PIM data. Akeneo is never created into — a SKU missing there is reported, not added."
      />

      {result && (
        <ResultsCard
          result={result}
          dryRun={resultWasDryRun}
          onDismiss={() => setResult(null)}
        />
      )}

      {selected.size > 0 && (
        <div className="pim-bulk-bar" style={{ position: 'sticky', top: 0, zIndex: 5, marginBottom: 12 }}>
          <Card>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
              <strong>{selected.size} selected</strong>
              <span className="pim-muted" style={{ fontSize: 12 }}>
                Max {MAX_BATCH} per push — the push runs while you wait.
              </span>
              <Button size="sm" onClick={() => runPush(false)} disabled={busy}>
                Push ({selected.size})
              </Button>
              <Button size="sm" variant="secondary" onClick={() => runPush(true)} disabled={busy}>
                Dry run
              </Button>
              <label className="pim-check" style={{ fontSize: 13 }}>
                <input
                  type="checkbox"
                  checked={includeImage}
                  onChange={(e) => setIncludeImage(e.target.checked)}
                />
                Include image
              </label>
              <label
                className="pim-check"
                style={{ fontSize: 13, opacity: includeImage ? 1 : 0.5 }}
                title="Re-upload even when Akeneo already has an image for the attribute"
              >
                <input
                  type="checkbox"
                  checked={forceImage}
                  disabled={!includeImage}
                  onChange={(e) => setForceImage(e.target.checked)}
                />
                Replace existing
              </label>
              <Button variant="ghost" size="sm" onClick={() => setSelected(new Set())}>
                Clear
              </Button>
            </div>
          </Card>
        </div>
      )}

      <Card flush>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', padding: 14, alignItems: 'center' }}>
          <SearchInput
            value={qDraft}
            onChange={(v) => { setOffset(0); setQDraft(v); }}
            placeholder="SKU or name — wildcards * and ?"
            style={{ minWidth: 340 }}
          />
          <select
            value={familyCode}
            onChange={(e) => { setOffset(0); setFamilyCode(e.target.value); }}
          >
            <option value="">All families</option>
            {families.map((f) => (
              <option key={f.code} value={f.code}>{f.label}</option>
            ))}
          </select>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
            <button
              type="button"
              className={`pim-tag-chip${status === '' ? ' active' : ''}`}
              onClick={() => { setOffset(0); setStatus(''); }}
            >
              All{typeof summary.total === 'number' ? ` (${summary.total})` : ''}
            </button>
            {STATUS_FILTERS.map((f) => {
              const n = f.count ? summary[f.count] : undefined;
              return (
                <button
                  type="button"
                  key={f.key}
                  className={`pim-tag-chip${status === f.key ? ' active' : ''}`}
                  onClick={() => { setOffset(0); setStatus(f.key); }}
                >
                  {f.label}
                  {typeof n === 'number' ? ` (${n})` : ''}
                </button>
              );
            })}
          </div>
        </div>
        {truncated && (
          <p className="pim-muted" style={{ padding: '0 14px 12px', margin: 0, fontSize: 13 }}>
            Showing the first slice of the pushed catalogue only — narrow the search to see the rest.
          </p>
        )}
        <DataTable
          columns={columns}
          rows={rows}
          rowKey={(r) => r.identifier}
          loading={loading}
          empty={
            <EmptyState
              title="Nothing to push"
              body={(status && EMPTY_HINTS[status]) || 'No products match the current filters.'}
            />
          }
        />
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: 14 }}>
          <span style={{ fontSize: 13, color: '#667' }}>
            {total} products · showing {total ? offset + 1 : 0}–{Math.min(offset + limit, total)}
          </span>
          <span style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 13, color: '#667' }}>
              Rows
              <select
                value={limit}
                onChange={(e) => { setOffset(0); setLimit(Number(e.target.value)); }}
              >
                {PAGE_SIZES.map((n) => (
                  <option key={n} value={n}>{n}</option>
                ))}
              </select>
            </label>
            <Button
              variant="secondary"
              size="sm"
              disabled={offset === 0}
              onClick={() => setOffset(Math.max(0, offset - limit))}
            >
              Prev
            </Button>
            <Button
              variant="secondary"
              size="sm"
              disabled={offset + limit >= total}
              onClick={() => setOffset(offset + limit)}
            >
              Next
            </Button>
          </span>
        </div>
      </Card>

      <Toast message={toast?.message} error={toast?.error} />
    </>
  );
}
